<template>
	<header>
		<div class="pc-header pc-center-1200" :class="{ 'pc-header--en': currentLocale === 'en' }">
			<div class="pc-header-left">
				<img
					class="logo"
					:src="headerLogoSrc"
					alt=""
					role="presentation"
					width="105"
					height="36"
					@click="goHome"
				/>
			</div>
			<div class="pc-header-center">
				<!-- 菜单栏 -->
				<ul class="menu-box">
					<li
						v-for="(item, index) in menuList"
						:key="item.path || item.elementId || index"
						:class="[
							menuSelected === index ? 'active' : '',
							item.hasDropdown ? 'has-dropdown' : '',
							item.dropdownDisabled ? 'dropdown-disabled' : '',
						]"
						@click="handleNavClick(item, index)"
						@mouseenter="item.hasDropdown && !item.dropdownDisabled ? showDropdown(index) : null"
						@mouseleave="item.hasDropdown && !item.dropdownDisabled ? hideDropdown(index) : null"
						style="position: relative"
					>
						<p>{{ item.name }}</p>
						<!-- 超大下拉菜单，仅商城Tab显示 -->
						<transition name="dropdown-fade">
							<div
								v-if="item.hasDropdown && dropdownVisibleIndex === index"
								class="super-dropdown-menu"
								@mouseenter="showDropdown(index)"
								@mouseleave="hideDropdown(index)"
							>
								<div class="dropdown-content">
									<div class="category-list1">
										<div class="title">{{ t("common.pcHeader.mall.effectTitle") }}</div>
										<div class="category-content">
											<div
												v-for="item in goodsCategoryList1"
												:key="item.id"
												@click="handleDropdownCategoryClick(item, 1)"
											>
												<p>{{ item.name }}</p>
											</div>
										</div>
									</div>

									<div class="category-list2">
										<div class="title">{{ t("common.pcHeader.mall.categoryTitle") }}</div>
										<div class="category-content">
											<div
												v-for="item in goodsCategoryList2"
												:key="item.id"
												@click="handleDropdownCategoryClick(item, 2)"
											>
												<p>{{ item.name }}</p>
											</div>
										</div>
									</div>

									<div class="drop-data">
										<div v-for="item in dropData" :key="item.id" @click="handleDropDataClick(item)">
											<img :src="item.cover" />
											<p>{{ item.name }}</p>
										</div>
									</div>
								</div>
							</div>
						</transition>
					</li>
				</ul>
			</div>
			<div class="pc-header-right">
				<div class="btn-search" @click="openSearchModal">
					<img src="./images/search.png" alt="" width="20" height="20" />
				</div>
				<!-- 下载app -->
				<div class="btn-downapp" @click="handleDownloadAppClick">
					{{ t("common.pcHeader.downloadApp") }}
					<!-- hover -->
					<!-- <div class="hover-downapp">
						<div class="hover-code-box">
							<img class="hover-code" :src="QrcodePicGm" />
							<img class="hover-code" :src="QrcodePicIOS" />
						</div>
						<div class="hover-btn-box">
							<img @click.stop="toAppStore('android')" src="./images/app_gm.png" />
							<img @click.stop="toAppStore('ios')" src="./images/app_ios.png" />
						</div>
					</div> -->
				</div>

				<!-- 语言切换 -->
				<div class="lang-switch" role="group" :aria-label="t('common.pcHeader.languageSwitch')">
					<button
						type="button"
						class="lang-switch-item"
						:class="{ active: currentLocale === 'zh-TW' }"
						aria-pressed="currentLocale === 'zh-TW'"
						@click="switchLanguage('zh-TW')"
					>
						繁
					</button>
					<button
						type="button"
						class="lang-switch-item lang-switch-item--en"
						:class="{ active: currentLocale === 'en' }"
						aria-pressed="currentLocale === 'en'"
						@click="switchLanguage('en')"
					>
						En
					</button>
				</div>

				<!-- 鉴权未就绪：占位，避免先闪登录按钮导致布局抖动 -->
				<div v-if="!authReady" class="auth-area" aria-hidden="true">
					<div class="auth-placeholder" />
				</div>
				<!-- 未登录状态 -->
				<div v-else-if="!isLogin" class="auth-area">
					<div class="btn-login" @click="loginHandler">
						{{ t("common.pcHeader.loginRegister") }}
					</div>
				</div>
				<!-- 已登录状态 -->
				<section v-else class="auth-area user-tool-box">
					<!-- 消息入口 -->
					<!-- 有未读消息显示红点 -->
					<div class="message-box" @click="goMessagePage">
						<img :src="messageIcon" alt="" width="24" height="24" />
						<!-- <img src="@/assets/images/icon_message.png" />

						<img
							v-if="unReadMsgTotalCount > 0"
							class="red-dot"
							src="@/assets/images/icon_message_red.png"
						/> -->
					</div>
					<!-- 头像 -->
					<div class="avatar-box">
						<n-image preview-disabled @click="goUserCenter" class="avatar" :src="userInfo.avatar">
							<template #placeholder>
								<IconImageOutLine width="35" height="35" />
							</template>
						</n-image>
					</div>
				</section>
			</div>
		</div>
	</header>
	<ClientOnly>
		<PcSearchModal
			v-if="showSearchModal"
			v-model:show="showSearchModal"
			:search-tracking-scope="searchTrackingScope"
			:trigger-position="searchTriggerPosition"
		/>
	</ClientOnly>
</template>

<script setup>
import { inject, onMounted, ref, watch, computed } from "vue";
import { NImage } from "naive-ui";
import { storeToRefs } from "pinia";

import IconImageOutLine from "@/components/IconImageOutLine/index.vue";
import { downloadApp, removeDomain } from "~/utils/common";
import { useUserStore } from "~/stores/user";
import { useMsgCountStore } from "~/stores/msg-count";
import { logEvent } from "~/utils/firebase";
import {
	mapTrackingLanguage,
	useSiteComponentTracking,
} from "~/composables/useSiteComponentTracking";

import { getResourse } from "~/api/api";
import eventBus from "~/utils/eventBus";
import { COLD_START_TAGS_TAB_SWITCH_EVENT } from "~/utils/coldStartTags";

import QrcodePicGm from "./images/qrcode_link_gm.png";
import QrcodePicIOS from "./images/qrcode_link_ios.png";
import { useI18n } from "~/composables/useI18n";
import { stripLocalePrefix, addLocalePrefix } from "~/utils/i18n-locale-routes";
import { getRuntimeLocale } from "~/i18n/index";
import headerLogoZh from "~/assets/images/SeerQtw.svg";
import headerLogoEn from "~/assets/images/SeerQ.svg";
import messageIcon from "~/assets/images/message-new.png";
import PcSearchModal from "./pc-search-modal.vue";
import { useAppDownloadModal } from "~/composables/useAppDownloadModal";
import { useTaiwanIp } from "~/composables/useTaiwanIp";

const showSearchModal = ref(false);
const { openAppDownloadModal } = useAppDownloadModal();
const { checkTaiwanIp } = useTaiwanIp();

const route = useRoute(); // Nuxt's useRoute
const { track, trackHomepageView } = useSiteComponentTracking("pc");

const { locale, setLocale, t, trackTranslationDeps } = useI18n();
const currentLocale = computed(() => locale?.value || "zh-TW");
const headerLogoSrc = computed(() => (currentLocale.value === "en" ? headerLogoEn : headerLogoZh));

const localizeNavPath = path => {
	const localeValue = getRuntimeLocale();
	return addLocalePrefix(path, localeValue);
};

const isMediaSquareRoute = computed(() => {
	const path = stripLocalePrefix(route.path);
	return (
		path === "/seer/square" ||
		path.startsWith("/seer/square/") ||
		path.startsWith("/seer/square-detail/")
	);
});

const searchTrackingScope = computed(() => (isMediaSquareRoute.value ? "media_square" : ""));

const searchTriggerPosition = computed(() => {
	const path = stripLocalePrefix(route.path);

	if (path.startsWith("/seer/square-detail/")) {
		return "breadcrumb_search";
	}

	if (!isMediaSquareRoute.value) return "top_search";

	if (path === "/seer/square" || path === "/seer/square/") {
		return "square_home_search";
	}

	return "square_page_search";
});

const switchLanguage = async lang => {
	if (currentLocale.value === lang) return;
	track("language_switch_click", {
		from_language: mapTrackingLanguage(currentLocale.value),
		to_language: mapTrackingLanguage(lang),
	});
	await setLocale(lang);
};

const handleDropDataClick = item => {
	navigateTo(item.jump_url);
};

const GOODS_CATEGORY_LIST2_DEFS = [
	{ id: 23, nameKey: "bracelet" },
	{ id: 35, nameKey: "hangings" },
	{ id: 24, nameKey: "necklace" },
	{ id: 26, nameKey: "ornament" },
	{ id: 25, nameKey: "talisman" },
	{ id: 37, nameKey: "zodiac" },
];

const GOODS_CATEGORY_LIST1_DEFS = [
	{
		id: 28,
		nameKey: "wealth",
		image_url: "https://img-fe.seersecret.com/image/d7f2d605cd571c53d3889a292af7547a-80x80.png",
	},
	{
		id: 30,
		nameKey: "protection",
		image_url: "https://img-fe.seersecret.com/image/a59b2af458038347b83d6502ecb9836d-81x80.png",
	},
	{
		id: 31,
		nameKey: "relationship",
		image_url: "https://img-fe.seersecret.com/image/8cb6b2fd02c7939b73002d6ca27d02ee-81x80.png",
	},
	{
		id: 32,
		nameKey: "peace",
		image_url: "https://img-fe.seersecret.com/image/246c0942b93cea842eb2cc593773e1b7-81x80.png",
	},
	{
		id: 33,
		nameKey: "career",
		image_url: "https://img-fe.seersecret.com/image/002275c2cbad6b9681b0e0c473f10d14-81x80.png",
	},
];

const goodsCategoryList2 = computed(() => {
	trackTranslationDeps();
	return GOODS_CATEGORY_LIST2_DEFS.map(item => ({
		...item,
		name: t(`common.pcHeader.mall.category.${item.nameKey}`),
	}));
});

const goodsCategoryList1 = computed(() => {
	trackTranslationDeps();
	return GOODS_CATEGORY_LIST1_DEFS.map(item => ({
		...item,
		name: t(`common.pcHeader.mall.effect.${item.nameKey}`),
	}));
});

// 是否有未读消息
const { unReadMsgTotalCount } = storeToRefs(useMsgCountStore()); //未读消息数
const goMessagePage = () => {
	track("message_icon_click", {
		element_id: "message_icon",
	});
	navigateToUserPage();
};

const navigateToUserPage = async () => {
	await navigateTo("/seer/user?type=message");
};

// 菜单栏
const menuSelected = ref("");
const MENU_DEFS = [
	{
		path: "/",
		nameKey: "home",
		elementId: "nav_home",
	},
	{
		path: "/seer/square",
		nameKey: "square",
		elementId: "nav_square",
	},
	{
		path: "/seer/service-institution",
		nameKey: "serviceInstitution",
		elementId: "nav_service_institution",
	},
	{
		path: "/seer/aboutus",
		nameKey: "about",
		elementId: "nav_about_us",
	},
];

// 菜单列表
const menuList = computed(() => {
	trackTranslationDeps();
	const base = MENU_DEFS.map(def => ({
		path: def.path,
		name: t(`common.pcHeader.nav.${def.nameKey}`),
		elementId: def.elementId,
		hasDropdown: def.hasDropdown,
		dropdownDisabled: def.dropdownDisabled,
	}));

	try {
		let hostname = "";
		if (import.meta.server) {
			const headers = useRequestHeaders(["host", "x-forwarded-host"]);
			hostname = headers["x-forwarded-host"] || headers["host"] || "";
		} else {
			const url = useRequestURL();
			hostname = url.hostname;
		}

		if (hostname.includes("seerify.shop")) {
			return base.filter(item => !["/seer/onlinecs", "/seer/master-fortune"].includes(item.path));
		}
	} catch (e) {
		console.log(e);
	}
	return base;
});

const findActiveMenuIndex = normalizedPath => {
	return menuList.value.findIndex(menu => {
		if (menu.path === normalizedPath) return true;

		if (menu.path === "/") {
			return (
				normalizedPath === "/seer/huangli" ||
				normalizedPath.startsWith("/seer/huangli/") ||
				normalizedPath === "/seer/daily-energy" ||
				normalizedPath.startsWith("/seer/daily-energy/") ||
				normalizedPath === "/seer/tests" ||
				normalizedPath.startsWith("/seer/tests/") ||
				normalizedPath === "/seer/master-fortune" ||
				normalizedPath.startsWith("/seer/master-fortune/") ||
				normalizedPath.startsWith("/seer/master-s") ||
				normalizedPath === "/seer/master-s/"
			);
		}

		if (menu.path === "/seer/square") {
			return (
				normalizedPath === "/seer/square" ||
				normalizedPath.startsWith("/seer/square/") ||
				normalizedPath === "/seer/square-detail" ||
				normalizedPath.startsWith("/seer/square-detail/")
			);
		}

		return false;
	});
};

// 下拉菜单显示控制
const dropdownVisibleIndex = ref(null);
function showDropdown(index) {
	dropdownVisibleIndex.value = index;
}
function hideDropdown(index) {
	if (dropdownVisibleIndex.value === index) {
		dropdownVisibleIndex.value = null;
	}
}

// 菜单栏点击
const handleNavClick = async (item, index) => {
	// 移除立即设置 menuSelected，让路由监听器来处理
	// 这样可以避免样式跳动问题
	if (item.path) {
		track("nav_item_click", {
			element_id: item.elementId,
			element_text: item.name,
			position: index + 1,
			target_url: item.path,
		});
		await navigateTo({ path: localizeNavPath(item.path) });
		if (item.path === "/seer/square" && isLogin.value) {
			eventBus.emit(COLD_START_TAGS_TAB_SWITCH_EVENT);
		}
	}
};

const initMenuSelect = () => {
	const normalizedPath = stripLocalePrefix(route.path);
	const matchingIndex = findActiveMenuIndex(normalizedPath);
	menuSelected.value = matchingIndex !== -1 ? matchingIndex : "";
};

// 监听路由变化（immediate：首屏即标记选中，避免 onMounted 后才亮起）
watch(
	() => route.path,
	() => {
		initMenuSelect();
	},
	{ immediate: true }
);

onMounted(() => {
	trackHomepageView();
});

// 用户信息
const useUser = useUserStore();
const { userInfo, isLogin, authReady } = storeToRefs(useUser);

// 登录按钮
const openLoginPopup = inject("openLoginPopup");
function loginHandler() {
	track("account_entry_click", {
		element_id: "account_icon",
	});
	if (route.path.includes("seer/guide-chat")) {
		// 假聊天室
		eventBus.emit("loginFromHeader");
	} else {
		openLoginPopup(undefined, null, true, "header_account");
	}
}

// 跳转个人中心
async function goUserCenter() {
	track("account_entry_click", {
		element_id: "account_icon",
	});
	await navigateTo("/seer/user");
}

// 跳转首页
async function goHome() {
	track("nav_logo_click", {
		element_id: "logo",
		target_url: "/",
	});
	await navigateTo(localizeNavPath("/"));
}

// 获取轮播图数据 - 服务端渲染
const { data: dropData } = await useLazyAsyncData(
	"header-drop-data",
	async () => {
		try {
			const res = await getResourse({ class: 128 });
			return res.data.map(v => ({
				...v,
				jump_url: removeDomain(v.actioncontent.global_type, 1),
			}));
		} catch (error) {
			console.error("加载轮播图失败:", error);
			return [];
		}
	},
	{
		default: () => [],
		server: true,
	}
);

// 处理下拉菜单分类点击
async function handleDropdownCategoryClick(item, categoryType) {
	// 隐藏下拉菜单
	hideDropdown(2); // 商城菜单的索引是2

	// 跳转到商城页面并携带分类参数
	const query = {
		category_id: item.id,
		category_type: categoryType,
	};

	logEvent("site_mall_category_click");
	await navigateTo({
		path: "/seer/mall",
		query: query,
	});
}

// 下載app
const toAppStore = type => {
	logEvent(type === "android" ? "site_googleplay_click" : "site_appstore_click");
	downloadApp(type);
};

async function handleDownloadAppClick() {
	track("app_download_click", {
		element_id: "download_app_top",
		store_type: "unknown",
	});
	if (await checkTaiwanIp()) {
		await navigateTo("/seer/app-landing");
		return;
	}
	openAppDownloadModal({ hideTitle: true });
}

function openSearchModal() {
	track("search_entry_click", {
		element_id: "top_search",
		trigger_position: searchTriggerPosition.value,
	});
	showSearchModal.value = true;
}
</script>

<style lang="scss" scoped>
header {
	position: sticky;
	top: 0;
	z-index: 100;
	background-color: #ffffff;
	font-family: Source Han Serif CN;
}

.pc-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 24px;
	width: 100%;
	max-width: 1200px;
	height: 80px;
	font-size: 18px;
	background-color: #ffffff;
	margin: 0 auto;

	&-left {
		display: flex;
		align-items: center;
		flex: 0 0 105px;
		width: 105px;
	}

	&-center {
		display: flex;
		align-items: center;
		flex: 1 1 auto;
		min-width: 0;
		justify-content: center;
	}

	&-right {
		display: flex;
		align-items: center;
		flex: 0 1 auto;
		gap: 22px;
		min-width: 0;
	}

	&--en &-right {
		gap: 16px;
	}

	.logo {
		flex-shrink: 0;
		width: 105px;
		height: 36px;
		object-fit: contain;
		cursor: pointer;
	}

	.auth-area {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		flex: 0 0 auto;
		width: 94px;
		min-width: 94px;
		min-height: 36px;
		box-sizing: border-box;
	}

	&--en .auth-area {
		width: 168px;
		min-width: 168px;
	}

	/* 英文已登录：按图标实际宽度，避免与语言切换之间留出大块空白 */
	&--en .auth-area.user-tool-box {
		width: auto;
		min-width: 0;
		justify-content: flex-start;
	}

	.auth-placeholder {
		width: 100%;
		height: 36px;
		border-radius: 18px;
		background: #f4f4f4;
	}

	.menu-box {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 30px;
		min-width: 0;

		li {
			cursor: pointer;
			font-size: 18px;
			color: #333333;
			/* 覆盖全局宋体 !important，避免网页字体加载时从细到粗导致菜单宽度抖动 */
			font-family:
				"PingFang SC", "PingFang TC", "Microsoft YaHei", "Heiti TC", sans-serif !important;
			font-weight: 500;
			font-synthesis: none;

			p {
				font-family: inherit !important;
				font-weight: inherit;
			}

			&:first-child {
				margin: 0 20px;
			}
			&:hover {
				color: #f2af19;
			}
			&.active {
				color: #f2af19;
			}
		}
	}

	&-right {
		display: flex;
		align-items: center;
		font-size: 18px;
	}

	.user-tool-box {
		display: flex;
		align-items: center;
		width: 94px;
		box-sizing: border-box;
	}

	&--en .user-tool-box {
		width: auto;
	}

	.message-box {
		width: 36px;
		height: 36px;
		margin-right: 14px;
		cursor: pointer;
		position: relative;
		background: #f0f0f0;
		box-sizing: border-box;
		padding: 6px;
		border-radius: 999px;
		flex-shrink: 0;

		img {
			display: block;
			width: 24px;
			height: 24px;
			object-fit: contain;
		}

		.red-dot {
			width: 14px;
			height: 14px;
			position: absolute;
			top: -2px;
			right: -2px;
		}
	}

	.avatar-box {
		position: relative;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		cursor: pointer;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		.avatar {
			width: 36px;
			height: 36px;
			transition: transform 0.6s ease;
			&:hover {
				transform: scale(1.2);
			}
		}
	}

	.btn-login {
		transition:
			background-color 0.1s ease,
			color 0.1s ease,
			border-color 0.1s ease;
		display: flex;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		width: 94px;
		height: 36px;
		background: #f4f4f4;
		border-radius: 999px;
		border: 1px solid #ebebeb;
		font-family: "PingFang SC", "PingFang TC", "Microsoft YaHei", "Heiti TC", sans-serif !important;
		font-weight: 400;
		font-synthesis: none;
		font-size: 16px;
		color: #595959;
		flex: 0 0 auto;
		white-space: nowrap;
		box-sizing: border-box;
	}

	&--en .btn-login {
		width: 168px;
	}

	.btn-search,
	.btn-downapp {
		height: 36px;
		background: #333333;
		border-radius: 18px;
		transition:
			background-color 0.1s ease,
			color 0.1s ease;
		display: flex;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		position: relative;
		font-family: "PingFang SC", "PingFang TC", "Microsoft YaHei", "Heiti TC", sans-serif !important;
		font-weight: 500;
		font-synthesis: none;
		font-size: 16px;
		color: #ffffff;
		padding: 0 15px;
		flex: 0 0 auto;
		white-space: nowrap;

		img {
			width: 20px;
			height: 20px;
			display: block;
			object-fit: contain;
		}
	}

	&--en .btn-search,
	&--en .btn-downapp {
		padding: 0 30px;
	}

	.btn-downapp {
		margin-right: 0;
		min-width: 118px;
	}

	&--en .btn-downapp {
		min-width: 148px;
	}

	.lang-switch {
		display: inline-flex;
		border-radius: 999px;
		overflow: hidden;
		margin-right: 0;
		flex-shrink: 0;
		box-sizing: border-box;
		height: 36px;
		background: #f4f4f4;

		&-item {
			width: 36px;
			height: 36px;
			padding: 0;
			border: none;
			background: #f4f4f4;
			color: #333333;
			font-size: 16px;
			font-weight: 500;
			font-synthesis: none;
			line-height: 34px;
			text-align: center;
			cursor: pointer;
			font-family:
				"PingFang SC", "PingFang TC", "Microsoft YaHei", "Heiti TC", sans-serif !important;
			border-radius: 999px;
			transition:
				background-color 0.2s ease,
				color 0.2s ease;

			&--en {
				font-family: "Times New Roman", Times, serif !important;
			}

			&.active {
				background: #333333;
				color: #ffffff;
			}
		}
	}

	.hover-downapp {
		position: absolute;
		top: 42px;
		right: -11px;
		z-index: 999;
		width: 255px;
		height: 260px;
		background: url("./images/bg.png") no-repeat center;
		background-size: 100% 100%;
		padding: 0 18px;
		box-sizing: border-box;
		box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
		display: none;
		opacity: 0;

		.hover-code-box {
			display: flex;
			padding-top: 10px;

			.hover-code {
				width: 90px;
				height: 90px;
				background: #ffffff;
				border-radius: 6px;
				padding: 4px;
				margin: 81px auto 10px;
				display: block;
			}
		}

		.hover-btn-box {
			display: flex;
			justify-content: space-between;
			img {
				width: 108px;
				height: 37px;
				cursor: pointer;
				transform: scale(0.95);
			}
		}
	}

	.btn-downapp:hover .hover-downapp,
	.hover-downapp:hover {
		display: block;
		opacity: 1;
		transition: all 0.3s ease;
	}
}
.super-dropdown-menu {
	position: absolute;
	left: calc(50% - 34px);
	top: 60px;
	transform: translateX(-50%);
	width: 1100px;
	min-height: 265px;
	background: #fff;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
	border-radius: 0 0 16px 16px;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 1;
	pointer-events: auto;
	transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.dropdown-content {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: flex-start;
	padding: 20px 40px;
	// justify-content: center;
	gap: 0 24px;

	.drop-data {
		display: flex;
		gap: 20px;
		position: relative;
		left: -40px;

		div:hover {
			p {
				color: #c67f34;
			}
		}

		p {
			font-size: 18px;
			color: #2b2f36;
		}

		img {
			width: 216px;
			height: 122px;
			border-radius: 8px 8px 8px 8px;
		}
	}

	.category-list1,
	.category-list2 {
		height: 100%;

		.category-content {
			display: flex;
			flex-wrap: wrap;
			width: 270px;
			gap: 0 20px;

			div {
				width: 125px;
				text-align: left;
				color: #2b2f36;
				padding: 2px;
				background: transparent;

				&:hover {
					padding: 2px;
					background: #fffaef;
					color: #c67f34;
				}
			}
		}

		.title {
			font-size: 28px;
			color: #2b2f36;

			text-align: left;
		}
	}

	.category-list1:hover {
		.title {
			color: #c67f34;
		}
	}

	.category-list2:hover {
		.title {
			color: #c67f34;
		}
	}
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
	transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
	opacity: 0;
}
.dropdown-fade-enter-to,
.dropdown-fade-leave-from {
	opacity: 1;
}
.has-dropdown {
	position: relative;
}
.dropdown-disabled {
	pointer-events: none;
	opacity: 0.5;
}

@media (max-width: 1180px) {
	.pc-header {
		gap: 16px;
		height: 72px;

		.menu-box {
			gap: 20px;

			li {
				font-size: 16px;

				&:first-child {
					margin: 0 8px;
				}
			}
		}

		&-right {
			gap: 10px;
		}

		.btn-search,
		.btn-downapp {
			height: 34px;
			padding: 0 18px;
			font-size: 14px;
		}

		.btn-login {
			width: auto;
			min-width: 104px;
			height: 34px;
			padding: 0 12px;
			font-size: 14px;
			white-space: nowrap;
		}

		.auth-area {
			min-width: 104px;
		}

		&--en .btn-login {
			min-width: 148px;
		}

		&--en .auth-area {
			min-width: 148px;
		}

		.lang-switch-item {
			width: 34px;
			height: 34px;
			font-size: 14px;
			line-height: 34px;
		}
	}
}

@media (max-width: 960px) {
	header {
		position: sticky;
	}

	.pc-header {
		flex-wrap: wrap;
		align-content: center;
		height: auto;
		min-height: 96px;
		padding-top: 10px;
		padding-bottom: 10px;

		&-left {
			order: 1;
		}

		&-right {
			order: 2;
			margin-left: auto;
		}

		&-center {
			order: 3;
			flex-basis: 100%;
			justify-content: flex-start;
			overflow-x: auto;
			scrollbar-width: none;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		.menu-box {
			justify-content: flex-start;
			gap: 22px;
			width: max-content;

			li {
				font-size: 15px;

				&:first-child {
					margin: 0;
				}
			}
		}

		.btn-downapp {
			display: none;
		}
	}
}

@media (max-width: 720px) {
	.pc-header {
		padding-left: 16px;
		padding-right: 16px;

		.btn-search {
			width: 34px;
			padding: 0;
		}

		.btn-login {
			max-width: 116px;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&--en .btn-login {
			max-width: none;
			min-width: 136px;
			overflow: visible;
			text-overflow: unset;
		}
	}
}
</style>
